import assert from 'minimalistic-assert';
import { SwapMode } from '../../common/types/index.js';
import { isExpired } from '../../common/utils/is-expired.js';
import { deconstructPairId } from '../../utils.js';
import { BRIDGE_ASSET_ID, DEFAULT_SLIPPAGE, STRATEGY, SVC } from './constants.js';
import { serializeQuoteKey } from '../utils.js';
import { QUOTE_CACHE_TTL } from '../constants.js';
import { ExchangeError, MissingPairError, MissingToAssetError, ProviderQuoteError, } from '../../errors.js';
import { FEE_ASSET_BY_CHAIN_ID, LOGS } from '../../constants.js';
import { retry } from '@exodus/simple-retry';
import { fromJSON } from '../../transaction/factory.js';
class Chained {
    swapStrategy = STRATEGY;
    sources = ['chained'];
    #assetsService;
    #fetchival;
    #tracer;
    #appName;
    #appVersion;
    #baseUrl;
    #cachedQuotes = new Map();
    #assetAvailabilityService;
    constructor(params) {
        this.#assetsService = params.assetsService;
        this.#fetchival = params.fetchival;
        this.#tracer = params.tracer;
        this.#appName = params.appName;
        this.#appVersion = params.appVersion;
        this.#baseUrl = params.baseUrl;
        this.#assetAvailabilityService = params.assetAvailabilityService;
    }
    async isPairSupported(pairId) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset, bridgeAsset] = await Promise.all([
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
            this.#assetsService.getOneById(BRIDGE_ASSET_ID),
        ]);
        if (!fromAsset || !toAsset || !bridgeAsset) {
            return false;
        }
        const isSameToken = fromAsset.id === toAsset.id;
        const isFromBridgeAsset = fromAsset.id === bridgeAsset.id;
        const isToBridgeAsset = toAsset.id === bridgeAsset.id;
        const isFromBaseAsset = !fromAsset[fromAsset.network];
        if (isSameToken || isFromBridgeAsset || isToBridgeAsset || isFromBaseAsset) {
            return false;
        }
        return this.#assetAvailabilityService.isPairSupportedBySource('chained', pairId);
    }
    async getQuote(params) {
        const { amount, pairId, slippage, swapMode = SwapMode.ExactIn, expiryMarginMs = 0 } = params;
        const quoteCacheKey = serializeQuoteKey({ amount, pairId, slippage, swapMode });
        const cachedQuote = this.#cachedQuotes.get(quoteCacheKey);
        if (!cachedQuote || isExpired(cachedQuote.quote.expiry, expiryMarginMs)) {
            const quote = await this._getQuote(params);
            this.#cachedQuotes.set(quoteCacheKey, { quote, expiry: Date.now() + QUOTE_CACHE_TTL });
        }
        return this.#cachedQuotes.get(quoteCacheKey).quote;
    }
    async _getQuote(params) {
        const { amount, swapMode = SwapMode.ExactIn, pairId, slippage = DEFAULT_SLIPPAGE, fromAddress, toAddress, } = params;
        const { to } = deconstructPairId(pairId);
        const [quote, toAsset] = await Promise.all([
            this.#getQuoteFromServer({
                fromAddress,
                toAddress,
                pairId,
                amount,
                slippage,
                swapMode,
            }),
            this.#assetsService.getOneById(to),
        ]);
        if (!toAsset) {
            throw new MissingToAssetError({ toAsset: to });
        }
        const rate = swapMode === SwapMode.ExactIn
            ? quote.amount.div(amount.toDefaultString()).toDefaultString()
            : amount.div(quote.amount.toDefaultString()).toDefaultString();
        const isExactIn = swapMode === SwapMode.ExactIn;
        return {
            expiry: Date.now() + QUOTE_CACHE_TTL,
            fromAmount: isExactIn ? amount : quote.amount,
            toAmount: isExactIn ? quote.amount : amount,
            swapStrategy: this.swapStrategy,
            rateId: quote.id,
            rate,
            exchangeFeeData: {},
            exchangeTxData: {},
            features: quote.features,
        };
    }
    async exchange(params) {
        let orderId;
        try {
            const { pairId, fromAddress, toAddress, swapMode = SwapMode.ExactIn, simulateTx, quote, fromAmount, toAmount, slippage = DEFAULT_SLIPPAGE, } = params;
            const { fromAsset, toAsset } = await this.#validateAndFetchAssets(pairId);
            const chainedOrder = await this.#createChainedOrder({
                quoteId: quote.rateId,
                pairId,
                fromAmount,
                toAmount,
                slippage,
                swapMode,
                fromAddress,
                toAddress,
            });
            orderId = chainedOrder.id;
            const transactions = await this.#processChainedOrderTransactions(chainedOrder);
            await this.#validateTransactions(transactions, {
                fromAsset,
                toAsset,
                fromAddress,
                swapMode,
                slippage,
                fromAmount: fromAsset.currency.defaultUnit(chainedOrder.fromAmount.value),
                toAmount: quote.toAmount,
                toAddress,
                simulateTx,
                chainedOrder,
            });
            if (chainedOrder.features?.includes('server-broadcast')) {
                return await this.#executeServerBroadcast({ params, fromAsset, chainedOrder });
            }
            const swapTxId = await this.#executeTransactions(transactions, {
                fromAsset,
                fromAmount: fromAsset.currency.defaultUnit(chainedOrder.fromAmount.value),
                fromAddress,
                toAddress,
                createAndSendTx: params.createAndSendTx,
                signAndSendTx: params.signAndSendTx,
                createAndSendEvmTx: params.createAndSendEvmTx,
            });
            await this.#updateChainedOrderWithSwapTx(orderId, swapTxId);
            return {
                transactions: [{ id: swapTxId, type: 'swapTransaction' }],
                svc: SVC,
                providerOrderId: orderId,
                orderId,
            };
        }
        catch (err) {
            if (!err.orderId && orderId) {
                err.orderId = orderId;
            }
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message, orderId }, { cause: err });
        }
    }
    async #executeServerBroadcast({ params, fromAsset, chainedOrder, }) {
        assert(params.signTx, 'signTx callback is required when server-broadcast feature is enabled');
        if (!chainedOrder.solanaTransactions) {
            throw new ExchangeError({
                message: 'server-broadcast feature is only supported for Solana transactions',
            });
        }
        if (chainedOrder.solanaTransactions.length === 0) {
            throw new ExchangeError({
                message: 'server-broadcast order has no transactions to sign',
            });
        }
        const signedTransactions = [];
        for (const tx of chainedOrder.solanaTransactions) {
            const { signedTx } = await this.#withTimeTracker('SignTx', () => params.signTx({ assetId: fromAsset.id, rawTx: tx.data }));
            signedTransactions.push(signedTx);
        }
        const { accepted } = await this.#withTimeTracker('ExecuteTrade', () => this.#executeChainedOrderOnServer(chainedOrder.id, signedTransactions));
        if (!accepted) {
            throw new ExchangeError({
                message: 'Server rejected server-broadcast chained order',
                orderId: chainedOrder.id,
            });
        }
        return {
            transactions: [],
            svc: SVC,
            orderId: chainedOrder.id,
            providerOrderId: chainedOrder.id,
        };
    }
    async getLimits(params) {
        const { from, to } = deconstructPairId(params.pairId);
        const url = new URL(`${this.#baseUrl}/chained-orders/limits`);
        const [limits, fromAsset, toAsset] = await Promise.all([
            this.#request(url).get({ pairId: params.pairId }),
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId: params.pairId, fromAsset, toAsset });
        }
        return [
            {
                [SwapMode.ExactIn]: {
                    min: fromAsset.currency.defaultUnit(limits.ExactIn.min.value),
                    max: fromAsset.currency.defaultUnit(limits.ExactIn.max.value),
                },
                [SwapMode.ExactOut]: {
                    min: toAsset.currency.defaultUnit(limits.ExactOut.min.value),
                    max: toAsset.currency.defaultUnit(limits.ExactOut.max.value),
                },
            },
        ];
    }
    setGeolocation(_geolocation) { }
    setTransactionPrivacyNetworks(_transactionPrivacyNetworks) { }
    async #validateAndFetchAssets(pairId) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({ pairId, fromAsset, toAsset });
        }
        return { fromAsset, toAsset };
    }
    async #createChainedOrder(params) {
        this.#traceMessage(LOGS.CREATE_CHAINED_ORDER);
        return this.#withTimeTracker('CreateOrder', async () => this.#request(new URL(`${this.#baseUrl}/chained-orders`)).post({
            quoteId: params.quoteId,
            pairId: params.pairId,
            fromAmount: params.fromAmount.toDefaultString(),
            toAmount: params.toAmount.toDefaultString(),
            slippage: params.slippage,
            swapMode: params.swapMode,
            fromAddress: params.fromAddress,
            toAddress: params.toAddress,
        }));
    }
    async #processChainedOrderTransactions(chainedOrder) {
        this.#traceMessage(LOGS.DECODING_TX);
        const feeAssetId = 'evmTransactions' in chainedOrder && chainedOrder.evmTransactions?.[0]
            ? FEE_ASSET_BY_CHAIN_ID.get(chainedOrder.evmTransactions[0].chainId)
            : undefined;
        const feeAsset = feeAssetId ? await this.#assetsService.getOneById(feeAssetId) : undefined;
        const transactions = chainedOrder.solanaTransactions?.map((tx) => fromJSON({ ...tx, type: 'solana', action: tx.type })) ||
            chainedOrder.evmTransactions?.map((tx) => fromJSON({
                ...tx,
                type: 'evm',
                action: tx.type,
                gasPrice: feeAsset?.currency.baseUnit(tx.gasPrice),
            }));
        if (!transactions) {
            throw new ExchangeError({ message: 'Missing transactions' });
        }
        return transactions;
    }
    async #validateTransactions(transactions, params) {
        this.#traceMessage(LOGS.CHECKING_ORDER_VALIDITY);
        const swapTransactions = transactions.filter((tx) => tx.action === 'swap');
        if (swapTransactions.length > 1) {
            const [firstSwap, secondSwap] = swapTransactions;
            if (!firstSwap || !secondSwap) {
                throw new ExchangeError({ message: 'Expected at least two swap transactions' });
            }
            if (firstSwap.kind === 'amm' && secondSwap.kind === 'rfq' && swapTransactions.length === 2) {
                return this.#validateAmmRfqChainedOrder({ firstSwap, secondSwap, params });
            }
            const lastSwap = swapTransactions.at(-1);
            const previousTxs = transactions.slice(0, transactions.lastIndexOf(lastSwap));
            return this.#withTimeTracker('CheckOrderIsValid', async () => lastSwap.validate({
                fromAsset: params.fromAsset,
                toAsset: params.toAsset,
                fromAddress: params.fromAddress,
                swapMode: params.swapMode,
                slippage: params.slippage,
                fromAmount: params.fromAmount,
                toAmount: params.toAmount,
                toAddress: params.toAddress,
                simulateTx: params.simulateTx,
                isChained: true,
                requiredPreviewTxs: previousTxs,
            }));
        }
        await this.#withTimeTracker('CheckOrderIsValid', async () => Promise.all(transactions.map(async (tx) => tx.validate({
            fromAsset: params.fromAsset,
            toAsset: params.toAsset,
            fromAddress: params.fromAddress,
            swapMode: params.swapMode,
            slippage: params.slippage,
            fromAmount: params.fromAmount,
            toAmount: params.toAmount,
            toAddress: params.toAddress,
            simulateTx: params.simulateTx,
            isChained: true,
            requiredPreviewTxs: transactions.filter((tx) => tx.action !== 'swap'),
        }))));
    }
    async #validateAmmRfqChainedOrder({ firstSwap, secondSwap, params, }) {
        const { chainedOrder } = params;
        const bridgeAsset = await this.#assetsService.getOneById(chainedOrder.bridgeAssetId);
        if (!bridgeAsset) {
            throw new ExchangeError({
                message: `Unknown bridge asset ${chainedOrder.bridgeAssetId}`,
                orderId: chainedOrder.id,
            });
        }
        const bridgeAmount = bridgeAsset.currency.defaultUnit(chainedOrder.bridgeAmount.value);
        await this.#withTimeTracker('CheckOrderIsValid', async () => Promise.all([
            firstSwap.validate({
                fromAsset: params.fromAsset,
                toAsset: bridgeAsset,
                fromAddress: params.fromAddress,
                swapMode: params.swapMode,
                slippage: params.slippage,
                fromAmount: params.fromAmount,
                toAmount: bridgeAmount,
                toAddress: params.toAddress,
                simulateTx: params.simulateTx,
                isChained: true,
            }),
            secondSwap.validate({
                fromAsset: bridgeAsset,
                toAsset: params.toAsset,
                fromAddress: params.fromAddress,
                swapMode: params.swapMode,
                slippage: params.slippage,
                fromAmount: bridgeAmount,
                toAmount: params.toAmount,
                toAddress: params.toAddress,
                simulateTx: params.simulateTx,
                isChained: true,
            }),
        ]));
    }
    async #executeTransactions(transactions, params) {
        return executeTransactions(transactions, params, {
            traceMessage: (msg) => this.#traceMessage(msg),
            withTimeTracker: (eventName, fn) => this.#withTimeTracker(eventName, fn),
        });
    }
    async #updateChainedOrderWithSwapTx(chainedOrderId, swapTxId) {
        this.#traceMessage(LOGS.UPDATING_ORDER);
        await this.#withTimeTracker('UpdateOrder', async () => this.#updateChainedOrder(chainedOrderId, swapTxId));
    }
    async #getQuoteFromServer(params) {
        const { from, to } = deconstructPairId(params.pairId);
        const quotesUrl = new URL(`${this.#baseUrl}/chained-orders/quotes`);
        quotesUrl.searchParams.set('extraFeatures', 'server-broadcast');
        const [fromAsset, toAsset, quote] = await Promise.all([
            this.#assetsService.getOneById(from),
            this.#assetsService.getOneById(to),
            this.#request(quotesUrl)
                .post({
                pairId: params.pairId,
                ...(params.swapMode === SwapMode.ExactIn
                    ? { fromAmount: params.amount.toDefaultString() }
                    : { toAmount: params.amount.toDefaultString() }),
                slippage: params.slippage,
                swapMode: params.swapMode,
                fromAddress: params.fromAddress,
                toAddress: params.toAddress,
            })
                .catch((err) => {
                throw new ProviderQuoteError({
                    message: `Unable to get quote from the server for ${params.amount.toDefaultString()} ${from} -> ${to} (swapMode: ${params.swapMode}): ${err.message}`,
                });
            }),
        ]);
        if (!fromAsset || !toAsset) {
            throw new ProviderQuoteError({
                message: `Unable to get quote from the server for ${params.amount.toDefaultString()} ${from} -> ${to} (swapMode: ${params.swapMode})`,
            });
        }
        let quotedAmount;
        if (params.swapMode === SwapMode.ExactOut) {
            quotedAmount = fromAsset.currency.defaultUnit(quote.fromAmount.value);
        }
        else {
            quotedAmount = toAsset.currency.defaultUnit(quote.toAmount.value);
        }
        return {
            expiry: quote.expiry,
            amount: quotedAmount,
            id: quote.id,
            swapStrategy: this.swapStrategy,
            rateId: quote.id,
            features: quote.features ?? [],
        };
    }
    async #updateChainedOrder(chainedOrderId, transactionId) {
        const doFetch = async () => this.#request(new URL(`${this.#baseUrl}/chained-orders/${encodeURIComponent(chainedOrderId)}`)).patch({ transactionId });
        return retry(doFetch, { delayTimesMs: ['500ms', '1s'] })();
    }
    async #executeChainedOrderOnServer(chainedOrderId, signedTransactions) {
        return this.#request(new URL(`${this.#baseUrl}/chained-orders/${encodeURIComponent(chainedOrderId)}/execute`)).post({ signedTransactions });
    }
    #request(url, { anonymousId } = {}) {
        return this.#fetchival(url, {
            headers: {
                'App-Name': this.#appName,
                'App-Version': this.#appVersion,
                ...(anonymousId ? { anonymousId } : {}),
            },
        });
    }
    #traceMessage(message) {
        this.#tracer?.traceMessage(message);
    }
    async #withTimeTracker(eventName, fn) {
        try {
            this.#tracer?.traceTime(eventName);
            return await fn();
        }
        finally {
            this.#tracer?.traceTimeEnd(eventName);
        }
    }
}
export async function executeTransactions(transactions, params, trace) {
    trace.traceMessage(LOGS.SENDING_TX);
    let swapTxId = null;
    for (const tx of transactions) {
        const action = tx.action;
        const eventName = action === 'approve' ? 'ApproveTokens' : action === 'revoke' ? 'RevokeTokens' : 'SendTx';
        const txId = await trace.withTimeTracker(eventName, async () => tx.execute({
            fromAsset: params.fromAsset,
            createAndSendTx: params.createAndSendTx,
            signAndSendTx: params.signAndSendTx,
            createAndSendEvmTx: params.createAndSendEvmTx,
            amount: params.fromAmount,
            fromAddress: params.fromAddress,
            toAddress: params.toAddress,
        }));
        if (tx.action === 'swap') {
            swapTxId = txId;
        }
    }
    if (!swapTxId) {
        throw new ExchangeError({ message: 'Missing swap transaction' });
    }
    return swapTxId;
}
export default Chained;
